import type { ReactNode } from "react";
import {
  WebsiteCard,
  WebsiteContainer,
  WebsiteEyebrow,
  WebsiteHeading,
  WebsiteSection,
  WebsiteText,
} from "@-/dev.improve.haniverse.components.website";
import { LandingGlyphFormula, LandingGlyphOperator, LandingGlyphTile } from "#landing-glyph-tile";
import { LandingMnemonicStoryText } from "#landing-mnemonic-story-text";

type LandingMnemonicExample = {
  eyebrow: string;
  /** Quoted from the character's card, in the cards' mnemonic markup. */
  mnemonic: string;
  note: string;
  title: string;
  visual: ReactNode;
};

const LANDING_MNEMONIC_EXAMPLES: LandingMnemonicExample[] = [
  {
    eyebrow: "Remember",
    mnemonic:
      "Picture a lollipop: a [_stick_](丨) you put in your [_mouth_](口) to get to the [**center**](.).",
    note: "There’s no drilling 中 as “center”: you see the lollipop, and the meaning comes with it.",
    title: "Recall the scene, recall the meaning.",
    visual: (
      <LandingGlyphFormula
        parts={[
          { glyph: "丨", label: "stick" },
          { glyph: "口", label: "mouth" },
        ]}
        result={{ glyph: "中", label: "center" }}
      />
    ),
  },
  {
    eyebrow: "Recognize",
    mnemonic: "One can only blame [**oneself**](.) if [_drip_]() coffee gets in one’s [_eye_](目).",
    note: "自 is 目 with one more stroke on top. Its story says what that stroke is: the drip.",
    title: "Lookalikes stop being a coin flip.",
    visual: (
      <div
        aria-label="目 (eye) beside 自 (oneself), which adds one stroke on top"
        className="flex items-end gap-2.5"
        role="img"
      >
        <LandingGlyphTile glyph="目" label="eye" />
        <LandingGlyphOperator>vs</LandingGlyphOperator>
        <LandingGlyphTile glyph="自" isAccent label="oneself" />
      </div>
    ),
  },
  {
    eyebrow: "Write",
    mnemonic:
      "A powerful [_boxer_](𠃌) needs [**power**](.) in order to [_throw_](丿) their opponent out of the ring.",
    note: "Single strokes have names too. The story names both strokes of 力 in the order you write them.",
    title: "The story spells out the strokes.",
    visual: (
      <LandingGlyphFormula
        parts={[
          { glyph: "𠃌", label: "boxer" },
          { glyph: "丿", label: "throw" },
        ]}
        result={{ glyph: "力", label: "power" }}
      />
    ),
  },
];

/**
 * The first promise, directly under the hero: every character has a story. Three real stories
 * from the curriculum show what one buys you: recalling, recognizing and writing the character.
 */
export function LandingMnemonicSection() {
  return (
    <WebsiteSection
      // The hero's phones overlap the top of this band on phones and tablets.
      className="pt-28 pb-20 sm:pt-32 sm:pb-28 lg:pt-24"
      id="stories"
      spacing="none"
      tone="canvas"
    >
      <WebsiteContainer>
        <div className="grid gap-5 lg:grid-cols-2 lg:items-end lg:gap-16">
          <div>
            <WebsiteEyebrow>A story for every character</WebsiteEyebrow>
            <WebsiteHeading className="mt-4 max-w-[15ch]" level={2}>
              Strange shapes become scenes you won’t forget.
            </WebsiteHeading>
          </div>
          <WebsiteText className="max-w-[32rem]" size="lead">
            Haniverse gives every stroke and component a simple name, then strings those names into
            a short scene around the character’s meaning. Picture the scene, and the meaning, the
            shape and even the strokes come back with it.
          </WebsiteText>
        </div>

        <ul className="m-0 mt-12 grid list-none gap-5 p-0 sm:mt-16 md:grid-cols-3 lg:gap-6">
          {LANDING_MNEMONIC_EXAMPLES.map((example) => (
            <li key={example.eyebrow}>
              <WebsiteCard className="flex h-full flex-col">
                <WebsiteEyebrow>{example.eyebrow}</WebsiteEyebrow>
                <WebsiteHeading className="mt-3" level={3} size="label">
                  {example.title}
                </WebsiteHeading>
                <div className="mt-6">{example.visual}</div>
                <blockquote className="m-0 mt-6 border-l-2 border-website-accent/40 pl-4 text-[0.95rem] leading-7 text-black/80">
                  <LandingMnemonicStoryText mnemonic={example.mnemonic} />
                </blockquote>
                <WebsiteText className="mt-auto pt-5" size="caption">
                  {example.note}
                </WebsiteText>
              </WebsiteCard>
            </li>
          ))}
        </ul>
      </WebsiteContainer>
    </WebsiteSection>
  );
}
